// src/services/api/appointmentService.ts
import { api } from "./api";
import type { CustomRequestConfig } from "../../types/api";
import type {
  IAppointment,
  IAppointmentResponse,
  BookAppointmentResponse,
  BookAppointmentRequest,
} from "../../types/appointment";
import { config } from "../../config/environment";

const CLINIC_ID = config.clinic.id;

interface AppointmentSearchParams {
  doctorIds?: string[];
  startDate?: string;
  endDate?: string;
  status?: string;
}

interface SlotGenerationParams {
  doctorId: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  slotDuration: number;
}

export const appointmentService = {
  // Public endpoint - Get appointment slots with search parameters
  getAppointmentSlots: async (searchParams: AppointmentSearchParams) => {
    const queryParams = new URLSearchParams();

    if (searchParams.doctorIds?.length) {
      searchParams.doctorIds.forEach((id) =>
        queryParams.append("doctorIds[]", id)
      );
    }
    if (searchParams.startDate)
      queryParams.append("startDate", searchParams.startDate);
    if (searchParams.endDate)
      queryParams.append("endDate", searchParams.endDate);
    if (searchParams.status) queryParams.append("status", searchParams.status);

    //const response = await api.get<IAppointment[]>(
    //  `/${CLINIC_ID}/appointments?${queryParams}`
    //);

    const url = `/clinics/${CLINIC_ID}/appointments?${queryParams}`;
    console.log("API Base URL:", import.meta.env.VITE_API_BASE_URL);
    console.log("Full request URL:", import.meta.env.VITE_API_BASE_URL + url);

    const response = await api.get<IAppointment[]>(url);
    return response.data;
  },

  // Public endpoint - Book an appointment
  bookPatientAppointment: async (
    appointmentId: string,
    doctorId: string,
    patientDetails: { patientName: string; patientPhone: string }
  ): Promise<IAppointmentResponse> => {
    const url = `/clinics/${CLINIC_ID}/appointments/book`;
    const payload: BookAppointmentRequest = {
      appointmentId,
      doctorId,
      patientName: patientDetails.patientName,
      patientPhone: patientDetails.patientPhone,
    };

    const response = await api.post<BookAppointmentResponse>(url, payload);
    return response.data.appointment;
  },

  // Protected endpoint - Generate appointment slots
  generateAppointmentSlots: async (params: SlotGenerationParams) => {
    const config: CustomRequestConfig = {
      requireAuth: true,
    };

    const response = await api.post(
      `/clinics/${CLINIC_ID}/appointments/generate-slots`,
      params,
      config
    );
    return response.data.slots;
  },

  // Public endpoint - Check slot availability
  checkAppointmentSlotAvailability: async (
    doctorId: string,
    appointmentId: string
  ) => {
    const response = await api.get<{ isAvailable: boolean }>(
      `/clinics/${CLINIC_ID}/appointments/${appointmentId}/availability`,
      {
        params: { doctorId },
      }
    );
    return response.data.isAvailable;
  },

  // Protected endpoint - Update appointment status
  updateAppointmentStatus: async (
    appointmentId: string,
    fromStatus: string,
    toStatus: string,
    patientDetails?: { patientName: string; patientPhone: string }
  ) => {
    const config: CustomRequestConfig = {
      requireAuth: true,
    };

    const response = await api.patch(
      `/clinics/${CLINIC_ID}/appointments/${appointmentId}/status`,
      {
        fromStatus,
        toStatus,
        patientDetails,
      },
      config
    );
    return response.data.success;
  },

  // Protected endpoint - Get doctor's booked appointments
  getDoctorBookedAppointments: async (doctorId: string) => {
    const config: CustomRequestConfig = {
      requireAuth: true,
    };

    const response = await api.get<{ appointments: IAppointment[] }>(
      `/clinics/${CLINIC_ID}/doctors/${doctorId}/appointments`,
      {
        ...config,
        params: { status: ["scheduled", "ongoing"].join(",") },
      }
    );
    return response.data.appointments;
  },

  // Public endpoint - Get a single appointment by ID
  getAppointmentById: async (appointmentId: string) => {
    const response = await api.get<IAppointment>(
      `/clinics/${CLINIC_ID}/appointments/${appointmentId}`
    );
    return response.data;
  },
};
